import { useEffect, useLayoutEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import clsx from 'clsx';
import { ChatShell } from '@/components/Messenger/shell/ChatShell';
import './ChatPopover.css';
import { useOverlayAnimation } from '@/hooks/useOverlayAnimation';
import { useFloatingPanelDrag } from '@/hooks/useFloatingPanelDrag';
import { OverlayIntrinsicSize } from '@/components/ui/OverlayIntrinsicSize/OverlayIntrinsicSize';
import type {
  MessengerOpenAgentChatFromNotificationDetail,
  MessengerOpenThreadFromNotificationDetail,
} from '@/constants/messengerEvents';

/** Верхняя зона окна, за которую можно перетаскивать (без отдельной шапки). */
const CHAT_POPOVER_DRAG_ZONE_TOP_PX = 56;

interface ChatPopoverProps {
  anchorEl: HTMLElement | null;
  open: boolean;
  onClose: () => void;
  stateRef?: React.MutableRefObject<{ isExiting: boolean } | null>;
  openThreadIntent?: MessengerOpenThreadFromNotificationDetail | null;
  onConsumedOpenThreadIntent?: () => void;
  openAgentChatIntent?: MessengerOpenAgentChatFromNotificationDetail | null;
  onConsumedOpenAgentChatIntent?: () => void;
}

export function ChatPopover({
  anchorEl,
  open,
  onClose,
  stateRef,
  openThreadIntent = null,
  onConsumedOpenThreadIntent,
  openAgentChatIntent = null,
  onConsumedOpenAgentChatIntent,
}: ChatPopoverProps) {
  const shellPersistRef = useRef(false);
  if (open) shellPersistRef.current = true;

  const { shouldRender, contentClasses, requestClose, isExiting } = useOverlayAnimation(open, onClose, true);
  const panelVisible = shouldRender;

  const { shellRef, position, headerDragProps } = useFloatingPanelDrag({
    enabled: shellPersistRef.current,
    initialPosition: 'bottomRight',
    dragZoneTopPx: CHAT_POPOVER_DRAG_ZONE_TOP_PX,
    draggingHeaderClassName: 'chat-popover-shell_dragging',
  });

  if (stateRef) stateRef.current = { isExiting };
  useEffect(
    () => () => {
      if (stateRef) stateRef.current = null;
    },
    [stateRef]
  );

  useEffect(() => {
    if (!panelVisible) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') requestClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [panelVisible, requestClose]);

  /** До aria-hidden на parked-shell: фокус не должен оставаться внутри скрытого dialog. */
  useLayoutEffect(() => {
    if (!isExiting && panelVisible) return;

    const shell = shellRef.current;
    const active = document.activeElement;
    if (!shell || !active || !shell.contains(active)) return;

    if (anchorEl && typeof anchorEl.focus === 'function') {
      anchorEl.focus({ preventScroll: true });
      return;
    }

    if (active instanceof HTMLElement) {
      active.blur();
    }
  }, [isExiting, panelVisible, anchorEl, shellRef]);

  const popoverLive = open && !isExiting;

  if (!shellPersistRef.current) return null;

  const popoverContent = (
    <div
      ref={shellRef}
      className={clsx('chat-popover-shell', !panelVisible && 'chat-popover-shell--parked')}
      style={{
        position: 'fixed',
        left: position.left,
        top: position.top,
        zIndex: panelVisible ? 1000 : -1,
        visibility: panelVisible ? 'visible' : 'hidden',
        pointerEvents: panelVisible && !isExiting ? 'auto' : 'none',
      }}
      role="dialog"
      aria-label="Мессенджер"
      aria-hidden={!panelVisible}
      {...(panelVisible ? headerDragProps : {})}
    >
      <OverlayIntrinsicSize>
        <div className={clsx('chat-popover', panelVisible && contentClasses)}>
          <ChatShell
            onClose={requestClose}
            messengerReadReceiptsActive={popoverLive}
            messengerSurfaceVisible={popoverLive}
            openThreadIntent={openThreadIntent}
            onConsumedOpenThreadIntent={onConsumedOpenThreadIntent}
            openAgentChatIntent={openAgentChatIntent}
            onConsumedOpenAgentChatIntent={onConsumedOpenAgentChatIntent}
          />
        </div>
      </OverlayIntrinsicSize>
    </div>
  );

  return createPortal(popoverContent, document.body);
}
